import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  RiskDetails,
  SeverityEnum,
  ProbabilityEnum,
  ImpactAreaEnum,
  TypeEnum,
  RiskStatusEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers
} from '../../services/api';
import { getDefaultExpectedDate } from './raidFormUtils';

interface RiskDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: RiskDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean; // Add read-only mode for when review is submitted
  isFromOtherQuestionnaire?: boolean; // Indicates if item is from another questionnaire
  defaultAssigneeId?: string | number | null;
  initialPartialRaid?: boolean;
}

const RiskDialog: React.FC<RiskDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false, // Default to false for backward compatibility
  isFromOtherQuestionnaire = false,
  defaultAssigneeId,
  initialPartialRaid = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<RiskDetails>({
    description: '',
    severity: SeverityEnum.MEDIUM,
    probability: ProbabilityEnum.MEDIUM,
    impact_area: ImpactAreaEnum.EFFORT,
    type: TypeEnum.INTERNAL,
    mitigation_plan: '',
    person_responsible: '',
    status: RiskStatusEnum.OPEN,
  });
  const [formTitle, setFormTitle] = useState('');
  const [dateOccurrence, setDateOccurrence] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [actualDate, setActualDate] = useState('');
  const [contingencyProposedDate, setContingencyProposedDate] = useState('');
  const [contingencyActualDate, setContingencyActualDate] = useState('');
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when initialData changes
  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        // Filter out System User and inactive users
        const filteredUsers = response.data.filter((user: User) => 
          user.display_name !== 'System User' && 
          user.user_name !== 'system_user' &&
          user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Helper function to find user by ID
  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find(u => u.id.toString() === userId) || null;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        status: initialData.status || RiskStatusEnum.OPEN,
      });
      
      // Set dates from initialData if they exist
      if (initialData.date_of_occurrence) {
        setDateOccurrence(initialData.date_of_occurrence);
      }
      const legacyData = initialData as RiskDetails & {
        proposed_date?: string;
        contingency_proposed_date?: string;
      };
      if (initialData.proposed_date || legacyData.proposed_date) {
        setProposedDate(initialData.proposed_date || legacyData.proposed_date || '');
      }
      if (initialData.actual_date || legacyData.proposed_date) {
        setActualDate(initialData.actual_date || legacyData.proposed_date || '');
      }
      if (initialData.contingency_proposed_date || legacyData.contingency_proposed_date) {
        setContingencyProposedDate(
          initialData.contingency_proposed_date || legacyData.contingency_proposed_date || ''
        );
      }
      if (initialData.contingency_actual_date || legacyData.contingency_proposed_date) {
        setContingencyActualDate(
          initialData.contingency_actual_date || legacyData.contingency_proposed_date || ''
        );
      }
    } else {
      // Reset form for new entries
      setFormData({
        description: '',
        severity: SeverityEnum.MEDIUM,
        probability: ProbabilityEnum.MEDIUM,
        impact_area: ImpactAreaEnum.EFFORT,
        type: TypeEnum.INTERNAL,
        mitigation_plan: '',
        person_responsible: '',
        status: RiskStatusEnum.OPEN,
      });
      
      // Set current date as default for new items
      const currentDate = getCurrentDate();
      setDateOccurrence(currentDate);
      setProposedDate(currentDate);
      setActualDate(currentDate);
      setContingencyProposedDate(currentDate);
      setContingencyActualDate(currentDate);
    }
    
    // Set the title
    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
  }, [initialData, title, initialPartialRaid, itemId]);

  const applyPartialRaidDefaults = () => {
    if (initialData) return;
    if (!formData.person_responsible && defaultAssigneeId != null && defaultAssigneeId !== '') {
      setFormData(prev => ({
        ...prev,
        person_responsible: String(defaultAssigneeId),
      }));
    }
    if (!proposedDate) {
      setProposedDate(getDefaultExpectedDate());
    }
  };

  const handleTextChange = (field: keyof RiskDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSelectChange = (field: keyof RiskDetails) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    
    // Clear error when title is updated
    if (errors.title) {
      setErrors(prev => ({
        ...prev,
        title: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.person_responsible?.trim()) {
      newErrors.person_responsible = 'Responsible person is required';
    }

    if (!effectivePartialRaid) {
      if (!formData.mitigation_plan?.trim()) {
        newErrors.mitigation_plan = 'Mitigation plan is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Prepare the data with dates
      const submitData: RaidItemCreate = {
        entity_id: 0, // Will be set by parent component
        title: formTitle,
        raid_category: RaidCategoryEnum.RISK,
        partial_raid: effectivePartialRaid,
        details: {
          ...formData,
          date_of_occurrence: dateOccurrence || undefined,
          proposed_date: proposedDate || undefined,
          actual_date: actualDate || undefined,
          contingency_proposed_date: contingencyProposedDate || undefined,
          contingency_actual_date: contingencyActualDate || undefined,
        }
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting risk item:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? 'View Risk' : (itemId ? 'Update Risk' : 'Add New Risk')}
        {loading && (
          <CircularProgress
            size={20}
            sx={{
              marginLeft: 2,
              verticalAlign: 'middle'
            }}
          />
        )}
      </DialogTitle>
      
      <DialogContent dividers>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire 
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'
            }
          </Typography>
        )}
        {!readOnly && isCreating && (
          <FormControlLabel
            control={
              <Switch
                checked={partialRaid}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setPartialRaid(enabled);
                  if (enabled) {
                    applyPartialRaidDefaults();
                  }
                }}
              />
            }
            label="Partial RAID (show only required fields)"
            sx={{ mb: 1 }}
          />
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              value={formTitle}
              onChange={handleTitleChange}
              fullWidth
              required
              error={!!errors.title}
              helperText={errors.title || 'Brief title describing the risk'}
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleTextChange('description')}
              multiline
              rows={3}
              fullWidth
              required
              error={!!errors.description}
              helperText={errors.description || 'Detailed description of the risk'}
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={formData.severity}
                label="Severity"
                onChange={handleSelectChange('severity') as any}
                readOnly={readOnly}
                inputProps={{ readOnly }}
              >
                {Object.values(SeverityEnum).map((severity) => (
                  <MenuItem key={severity} value={severity}>
                    {severity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Probability</InputLabel>
                  <Select
                    value={formData.probability}
                    label="Probability"
                    onChange={handleSelectChange('probability') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(ProbabilityEnum).map((probability) => (
                      <MenuItem key={probability} value={probability}>
                        {probability}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Impact Area</InputLabel>
                  <Select
                    value={formData.impact_area}
                    label="Impact Area"
                    onChange={handleSelectChange('impact_area') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(ImpactAreaEnum).map((area) => (
                      <MenuItem key={area} value={area}>
                        {area}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={handleSelectChange('type') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(TypeEnum).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Occurrence"
                  type="date"
                  value={dateOccurrence}
                  onChange={(e) => setDateOccurrence(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleSelectChange('status') as any}
                    readOnly={readOnly}
                    inputProps={{ readOnly }}
                  >
                    {Object.values(RiskStatusEnum).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Mitigation Plan
                </Typography>
                <Divider />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Mitigation Plan"
                  value={formData.mitigation_plan}
                  onChange={handleTextChange('mitigation_plan')}
                  multiline
                  rows={3}
                  fullWidth
                  required
                  error={!!errors.mitigation_plan}
                  helperText={errors.mitigation_plan || 'Plan to mitigate the risk'}
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.display_name}
              value={findUserById(formData.person_responsible || '')}
              onChange={(event, newValue) => {
                setFormData(prev => ({ 
                  ...prev, 
                  person_responsible: newValue ? newValue.id.toString() : '' 
                }));
              }}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Person Responsible"
                  required
                  error={!!errors.person_responsible}
                  helperText={errors.person_responsible || 'Person responsible for mitigation'}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No users found"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="Planned Date"
              type="date"
              value={proposedDate}
              onChange={(e) => setProposedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Date"
                  type="date"
                  value={actualDate}
                  onChange={(e) => setActualDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Contingency Plan
                </Typography>
                <Divider />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Contingency Plan"
                  value={formData.contingency_plan || ''}
                  onChange={handleTextChange('contingency_plan')}
                  multiline
                  rows={3}
                  fullWidth
                  helperText="Optional plan to be implemented if the risk occurs"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.display_name}
                  value={findUserById(formData.contingency_person_responsible || '')}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      contingency_person_responsible: newValue ? newValue.id.toString() : '' 
                    }));
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contingency Person Responsible"
                      helperText="Person responsible for contingency"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No users found"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Planned Date"
                  type="date"
                  value={contingencyProposedDate}
                  onChange={(e) => setContingencyProposedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Date"
                  type="date"
                  value={contingencyActualDate}
                  onChange={(e) => setContingencyActualDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RiskDialog;
